import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";

import {
  BeamTruss,
  BeamTrussType,
  BeamTrussState,
  DEFAULT_BEAM_TRUSS_CONFIG,
} from "../types/beamTruss";
import { validateBeamLoadcaseReferences } from "../utils/structuralValidation";
import { useModelStore } from "@/store/useModelStore";

interface BeamTrussStore extends BeamTrussState {
  // ===== Core CRUD =====
  addBeamTruss: (
    beamIds: string[],
    type: BeamTrussType,
    pressureValue?: number,
    dx?: number,
    dy?: number,
    dz?: number,
    dxRight?: number,
    dyRight?: number,
    dzRight?: number,
    fx?: number,
    fy?: number,
    fz?: number,
    referenceNode?: "node1" | "node2",
    percentageFromNode?: number,
    type4PeakLoad?: number,
    type4Direction?: "dx" | "dy" | "dz",
    type4PeakPosition?: number,
    type4CurveType?: "parabolic" | "sinusoidal" | "custom",
    label?: string,
    torque?: number,
    torqueDirection?: "clockwise" | "anticlockwise"
  ) => string;

  removeBeamTruss: (trussId: string) => void;
  updateBeamTruss: (trussId: string, updates: Partial<BeamTruss>) => void;

  // ===== Beam assignment (NEW CORE LOGIC) =====
  applyLoadToBeams: (loadId: string, beamIds: string[]) => boolean;
  removeLoadFromBeam: (loadId: string, beamId: string) => void;

  // ===== Selection =====
  setSelectedBeamTruss: (trussId: string | null) => void;

  // ===== Visibility =====
  toggleBeamTrussVisibility: (trussId: string) => void;
  toggleAllBeamTrussVisibility: (visible?: boolean) => void;
  areAllBeamTrussesVisible: () => boolean;

  getTrussesForBeam: (beamId: number | string) => BeamTruss[];
  showTrussForBeam: (beamId: string) => void;
  hideTrussForBeam: (beamId: string) => void;

  // Utility functions
  getNextTrussLabel: (beamId: string) => string;
  calculateArrowLength: (pressureValue: number) => number; // Changed from calculateArrowSize to calculateArrowLength
  calculateArrowCount: (beamLength: number) => number;

  // Dual pressure functions
  showPressureInputs: (beamId: string) => void;
  applyPressure: (
    beamId: string,
    leftValue: number,
    rightValue: number
  ) => void;

  applyPredefinedLoadToBeam: (presets: any[]) => void;

  updateBeamVisual: (beamId: string) => void;

  cleanCorruptedData: () => void;
}

export const useBeamTrussStore = create<BeamTrussStore>((set, get) => ({
  beamTrusses: [],
  selectedBeamTrussId: null,

  addBeamTruss: (
    beamIds = [],
    type,
    pressureValue = 0,
    dx = 0,
    dy = 0,
    dz = 0,
    dxRight = 0,
    dyRight = 0,
    dzRight = 0,
    fx = 0,
    fy = 0,
    fz = 0,
    referenceNode = "node1",
    percentageFromNode = 50,
    type4PeakLoad,
    type4Direction,
    type4PeakPosition,
    type4CurveType,
    label,
    torque,
    torqueDirection
  ) => {
    const id = uuidv4();

    // Validate beam references exist
    if (beamIds && beamIds.length > 0) {
      const modelStore = useModelStore.getState();
      const existingBeamIds = modelStore.beams.map((b) => b.id);

      const validationErrors = validateBeamLoadcaseReferences(
        [
          {
            name: label || `Load ${get().beamTrusses.length + 1}`,
            targetIds: beamIds,
          },
        ],
        existingBeamIds
      );

      if (validationErrors.length > 0) {
        console.warn(
          "⚠️ Beam loadcase validation warning:",
          validationErrors[0].message
        );
        // Filter out invalid beam IDs
        beamIds = beamIds.filter((bid) => existingBeamIds.includes(bid));
      }
      // Note: Multiple loads on the same beam are allowed (different loadcases can have different loads on same beam)
    }

    set((state) => {
      // Ensure array
      const currentTrusses = Array.isArray(state.beamTrusses)
        ? state.beamTrusses
        : [];

      return {
        beamTrusses: [
          // ...state.beamTrusses,
          ...currentTrusses,
          {
            id,
            beamIds: beamIds ?? [],

            type,
            pressureValue,

            leftPressureValue: type === "dual_pressure" ? dx : undefined,
            rightPressureValue: type === "dual_pressure" ? dy : undefined,

            dx: type === "distributed_uniform" ? dx : undefined,
            dy: type === "distributed_uniform" ? dy : undefined,
            dz: type === "distributed_uniform" ? dz : undefined,

            dxLeft: type === "distributed_variable" ? dx : undefined,
            dyLeft: type === "distributed_variable" ? dy : undefined,
            dzLeft: type === "distributed_variable" ? dz : undefined,
            dxRight: type === "distributed_variable" ? dxRight : undefined,
            dyRight: type === "distributed_variable" ? dyRight : undefined,
            dzRight: type === "distributed_variable" ? dzRight : undefined,

            fx: type === "concentrated_load" ? fx : undefined,
            fy: type === "concentrated_load" ? fy : undefined,
            fz: type === "concentrated_load" ? fz : undefined,
            referenceNode:
              type === "concentrated_load" ? referenceNode : undefined,
            percentageFromNode:
              type === "concentrated_load" ? percentageFromNode : undefined,

            type4PeakLoad:
              type === "distributed_type4" ? type4PeakLoad : undefined,
            type4Direction:
              type === "distributed_type4" ? type4Direction : undefined,
            type4PeakPosition:
              type === "distributed_type4" ? type4PeakPosition : undefined,
            type4CurveType:
              type === "distributed_type4" ? type4CurveType : undefined,

            torque: type === "twisted_load" ? torque : undefined,
            torqueDirection:
              type === "twisted_load" ? torqueDirection : undefined,

            baseArrowLength: DEFAULT_BEAM_TRUSS_CONFIG.baseArrowLength,
            arrowCount: DEFAULT_BEAM_TRUSS_CONFIG.minArrowCount,
            visible: true,
            // label: label ?? `Load ${state.beamTrusses.length + 1}`,
            label: label ?? `Load ${currentTrusses.length + 1}`,
            color: DEFAULT_BEAM_TRUSS_CONFIG.defaultColor,
          },
        ],
      };
    });
    return id;
  },

  applyLoadToBeams: (loadId: string, beamIds: string[]) => {
    // Validate beam references exist
    if (beamIds && beamIds.length > 0) {
      const modelStore = useModelStore.getState();
      const existingBeamIds = modelStore.beams.map((b) => b.id);

      const validationErrors = validateBeamLoadcaseReferences(
        [{ name: `Load ${loadId}`, targetIds: beamIds }],
        existingBeamIds
      );

      if (validationErrors.length > 0) {
        console.warn(
          "⚠️ Beam loadcase validation warning:",
          validationErrors[0].message
        );
        // Filter out invalid beam IDs
        beamIds = beamIds.filter((bid) => existingBeamIds.includes(bid));
      }
      // Note: Multiple loads on the same beam are allowed (different loadcases can have different loads on same beam)
    }

    set((state) => {
      // DEFENSIVE: Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) =>
          t.id === loadId
            ? {
                ...t,
                beamIds: Array.from(new Set([...t.beamIds, ...beamIds])),
              }
            : t
        ),
      };
    });
    return true;
  },

  removeLoadFromBeam: (loadId: string, beamId: string) => {
    set((state) => {
      // DEFENSIVE: Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) =>
          t.id === loadId
            ? { ...t, beamIds: t.beamIds.filter((id) => id !== beamId) }
            : t
        ),
      };
    });
  },

  removeBeamTruss: (trussId: string) => {
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [], selectedBeamTrussId: null };
      }

      return {
        beamTrusses: state.beamTrusses.filter((t) => t.id !== trussId),
        selectedBeamTrussId:
          state.selectedBeamTrussId === trussId
            ? null
            : state.selectedBeamTrussId,
      };
    });
  },

  updateBeamTruss: (trussId: string, updates) => {
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) =>
          t.id === trussId ? { ...t, ...updates } : t
        ),
      };
    });
  },

  setSelectedBeamTruss: (trussId) => {
    set({ selectedBeamTrussId: trussId });
  },

  toggleBeamTrussVisibility: (trussId) => {
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) =>
          t.id === trussId ? { ...t, visible: !t.visible } : t
        ),
      };
    });
  },

  toggleAllBeamTrussVisibility: (visible) => {
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      const nextVisible =
        visible !== undefined
          ? visible
          : !state.beamTrusses.every((t) => t.visible);

      return {
        beamTrusses: state.beamTrusses.map((t) => ({
          ...t,
          visible: nextVisible,
        })),
      };
    });
  },

  areAllBeamTrussesVisible: () => {
    const trusses = get().beamTrusses;

    // Ensure array
    if (!Array.isArray(trusses)) {
      return false;
    }

    return trusses.length > 0 && trusses.every((t) => t.visible);
  },

  getTrussesForBeam: (beamId: number | string) => {
    const id = Number(beamId); // normalize input
    const { beamTrusses } = get();

    // Ensure beamTrusses is an array
    if (!Array.isArray(beamTrusses)) {
      console.warn("⚠️ beamTrusses is not an array, resetting to []");
      set({ beamTrusses: [] });
      return [];
    }

    return beamTrusses.filter((truss) => {
      // Check if beamIds exists and is an array
      if (!truss.beamIds || !Array.isArray(truss.beamIds)) {
        console.warn("⚠️ Invalid truss.beamIds (not an array):", truss);
        return false; // Skip this corrupted truss
      }
      return truss.beamIds
        .map(Number) // normalize stored data
        .includes(id);
    });
  },

  showTrussForBeam: (beamId: string) => {
    const id = Number(beamId);
    set((state) => {
      // DEFENSIVE: Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) => {
          // Check if beamIds is an array
          if (!Array.isArray(t.beamIds)) {
            console.warn("⚠️ Invalid truss.beamIds:", t);
            return t;
          }
          return t.beamIds.map(Number).includes(id)
            ? { ...t, visible: true }
            : t;
        }),
      };
    });
  },

  hideTrussForBeam: (beamId: string) => {
    const id = Number(beamId);
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) => {
          // Check if beamIds is an array
          if (!Array.isArray(t.beamIds)) {
            console.warn("⚠️ Invalid truss.beamIds:", t);
            return t;
          }

          return t.beamIds.map(Number).includes(id)
            ? { ...t, visible: false }
            : t;
        }),
      };
    });
  },

  getNextTrussLabel: (beamId: string) => {
    const id = Number(beamId);
    const { beamTrusses } = get();

    // Ensure array
    if (!Array.isArray(beamTrusses)) {
      return "Load 1";
    }

    const count = beamTrusses.filter((t) => {
      // defensive check
      if (!Array.isArray(t.beamIds)) {
        return false;
      }
      return t.beamIds.map(Number).includes(id);
    }).length;

    return `Load ${count + 1}`;
  },

  calculateArrowLength: (pressureValue: number) => {
    const base = DEFAULT_BEAM_TRUSS_CONFIG.baseArrowLength;
    if (pressureValue <= 0) return base;
    return base * Math.log(pressureValue + 1);
  },

  calculateArrowCount: (beamLength: number) => {
    const { minArrowCount, maxArrowCount, spacingFactor } =
      DEFAULT_BEAM_TRUSS_CONFIG;

    const count = Math.floor(beamLength / spacingFactor);
    return Math.max(minArrowCount, Math.min(maxArrowCount, count));
  },
  applyPressure: (beamId, leftValue, rightValue) => {
    const id = Number(beamId);
    set((state) => {
      // Ensure array
      if (!Array.isArray(state.beamTrusses)) {
        return { beamTrusses: [] };
      }

      return {
        beamTrusses: state.beamTrusses.map((t) => {
          if (!Array.isArray(t.beamIds)) {
            return t;
          }
          return t.type === "dual_pressure" &&
            t.beamIds.map(Number).includes(id)
            ? {
                ...t,
                leftPressureValue: leftValue,
                rightPressureValue: rightValue,
              }
            : t;
        }),
      };
    });
  },

  applyPredefinedLoadToBeam: (presets: any[]) => {
    console.log(
      "[BeamTruss] Restoring beam loads from save file:",
      presets?.length || 0
    );

    if (!Array.isArray(presets) || presets.length === 0) {
      console.log("ℹ️ No beam loads to restore");
      set({ beamTrusses: [] });
      return;
    }

    // Directly restore objects to preserve IDs and all properties
    const restoredTrusses: BeamTruss[] = [];

    presets.forEach((preset) => {
      if (!preset || !preset.type) {
        console.warn("⚠️ Invalid preset (missing type):", preset);
        return;
      }

      // Ensure beamIds is an array
      const beamIds = Array.isArray(preset.beamIds)
        ? preset.beamIds.map(String)
        : [];

      if (beamIds.length === 0) {
        console.warn("⚠️ Preset has no beamIds, skipping:", preset.id);
        return;
      }

      // Build the restored truss object with ORIGINAL ID
      const restored: BeamTruss = {
        id: preset.id || uuidv4(), // ← Preserve original ID!
        beamIds,
        type: preset.type,
        pressureValue: preset.pressureValue || 0,
        baseArrowLength:
          preset.baseArrowLength || DEFAULT_BEAM_TRUSS_CONFIG.baseArrowLength,
        arrowCount:
          preset.arrowCount || DEFAULT_BEAM_TRUSS_CONFIG.minArrowCount,
        visible: preset.visible !== undefined ? preset.visible : true,
        label: preset.label || `Load ${restoredTrusses.length + 1}`,
        color: preset.color || DEFAULT_BEAM_TRUSS_CONFIG.defaultColor,
      };

      // Add type-specific properties
      switch (preset.type) {
        case "distributed_uniform":
          restored.dx = preset.dx;
          restored.dy = preset.dy;
          restored.dz = preset.dz;
          break;

        case "distributed_variable":
          restored.dxLeft = preset.dxLeft;
          restored.dyLeft = preset.dyLeft;
          restored.dzLeft = preset.dzLeft;
          restored.dxRight = preset.dxRight;
          restored.dyRight = preset.dyRight;
          restored.dzRight = preset.dzRight;
          break;

        case "concentrated_load":
          restored.fx = preset.fx;
          restored.fy = preset.fy;
          restored.fz = preset.fz;
          restored.referenceNode = preset.referenceNode || "node1";
          restored.percentageFromNode = preset.percentageFromNode || 50;
          break;

        case "distributed_type4":
          restored.type4PeakLoad = preset.type4PeakLoad;
          restored.type4Direction = preset.type4Direction;
          restored.type4PeakPosition = preset.type4PeakPosition;
          restored.type4CurveType = preset.type4CurveType;
          break;

        case "twisted_load":
          restored.torque = preset.torque;
          restored.torqueDirection = preset.torqueDirection;
          break;

        case "dual_pressure":
          restored.leftPressureValue = preset.leftPressureValue;
          restored.rightPressureValue = preset.rightPressureValue;
          break;

        default:
          console.warn("⚠️ Unknown load type:", preset.type);
      }

      restoredTrusses.push(restored);
    });

    // Validate all restored beam references before setting
    const modelStore = useModelStore.getState();
    const existingBeamIds = modelStore.beams.map((b) => b.id);

    // Validate each loadcase
    restoredTrusses.forEach((truss) => {
      if (truss.beamIds && truss.beamIds.length > 0) {
        const validationErrors = validateBeamLoadcaseReferences(
          [{ name: truss.label, targetIds: truss.beamIds }],
          existingBeamIds
        );

        if (validationErrors.length > 0) {
          console.warn(
            "⚠️ Beam loadcase validation warning:",
            validationErrors[0].message
          );
          // Filter out invalid beam IDs
          truss.beamIds = truss.beamIds.filter((bid) =>
            existingBeamIds.includes(bid)
          );
        }
      }
    });

    // Remove any loadcases with no valid beams
    const validTrusses = restoredTrusses.filter((t) => t.beamIds.length > 0);

    if (validTrusses.length !== restoredTrusses.length) {
      console.warn(
        `⚠️ Filtered out ${restoredTrusses.length - validTrusses.length} loadcases with no valid beams`
      );
    }

    set({ beamTrusses: validTrusses });
    console.log("[BeamTruss] Restored beam loads:", validTrusses.length);
  },

  showPressureInputs: (beamId: string) => {
    console.log(`Showing pressure inputs for beam ${beamId}`);
  },
  updateBeamVisual: (_beamId: string) => {
    // Reactivity is automatic via Zustand
  },

  cleanCorruptedData: () => {
    set((state) => {
      if (!Array.isArray(state.beamTrusses)) {
        console.log("🔧 Resetting corrupted beamTrusses to []");
        return { beamTrusses: [] };
      }

      // Filter out any trusses with invalid beamIds
      const cleaned = state.beamTrusses.filter((truss) => {
        if (!Array.isArray(truss.beamIds)) {
          console.warn("🗑️ Removing corrupted truss:", truss);
          return false;
        }
        return true;
      });

      if (cleaned.length !== state.beamTrusses.length) {
        console.log(
          `Cleaned ${state.beamTrusses.length - cleaned.length} corrupted trusses`
        );
        return { beamTrusses: cleaned };
      }

      return state;
    });
  },
}));
